import { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import useReportLog from '../../../../hooks/useReportLog';
import useGroup from '../../../../zustand/useGroup';
import useAppliedReportFilter from '../../../../hooks/useAppliedReportFilter';
import ReportChart from '../reportChart';
import styled from 'styled-components';
import { Segmented, Select } from 'antd';
import {
  buildRpmFuelSpeedSchema,
  buildRpmFuelSpeedTable,
  createRunningTimeAt,
  getAggregatedLength,
  PdfChartData,
  RPM_FUEL_SPEED_Y_AXIS,
} from '../../pdf/reportPdfData';
import { formatChartTables } from './chartTableHeader';

interface IProps {
  isEMS?: boolean;
  isShowTitle?: boolean;
  isReportSchedule?: {
    startAt: number;
    endAt: number;
    interval: any;
    deviceType: string;
    assetId: string;
    timezone: number;
  };
  height?: number;
  compactControls?: boolean;
  // calculated by the backend for the PDF page (GET /report-log/pdf-data)
  precomputed?: PdfChartData;
}

const ChartRpmFuelSpeedRunning: React.FC<IProps> = ({
  isEMS,
  isShowTitle,
  isReportSchedule,
  height,
  compactControls,
  precomputed,
}) => {
  const { logs } = useReportLog();
  const { filter, asset, timezone } = useAppliedReportFilter();
  const [activeChartType, setActiveChartType] = useState<string>('default');
  const reportTimezone = isReportSchedule?.timezone ?? timezone;

  const yAxis = useMemo(() => {
    if (isEMS) {
      return RPM_FUEL_SPEED_Y_AXIS.filter((item) => item !== 'fuel');
    }

    return RPM_FUEL_SPEED_Y_AXIS;
  }, [isEMS]);

  const schema = useMemo(() => {
    if (precomputed) {
      return precomputed.schema;
    }

    return buildRpmFuelSpeedSchema({
      logs,
      isReportSchedule: !!isReportSchedule,
      assetDevices: asset.devices,
      assetType: asset.assetType,
      isEMS,
      activeChartType,
    });
  }, [
    precomputed,
    isEMS,
    activeChartType,
    asset.devices,
    asset.assetType,
    isReportSchedule,
    logs,
  ]);

  const dataTable = useMemo(() => {
    if (precomputed) {
      return { ...precomputed.table, aeLookup: null };
    }

    return buildRpmFuelSpeedTable({
      logs,
      schema,
      schedule: isReportSchedule,
      timezone: reportTimezone,
    });
  }, [precomputed, schema, logs, isReportSchedule, reportTimezone]);

  const tables = useMemo(
    () =>
      formatChartTables(
        dataTable,
        isReportSchedule?.startAt as number,
        reportTimezone
      ),
    [dataTable, isReportSchedule?.startAt, reportTimezone]
  );

  const aeRunningTimeAt = useMemo(
    () => createRunningTimeAt(dataTable.aeLookup),
    [dataTable.aeLookup]
  );

  const tableColumns = useMemo(() => {
    let res: any[] = ['Time'];
    schema.forEach((item) => {
      res.push(item.seriesOption.name);
    });

    return res;
  }, [schema]);

  const aggregatedLength = useMemo(() => {
    if (isReportSchedule) {
      return getAggregatedLength({
        startAt: isReportSchedule.startAt,
        endAt: isReportSchedule.endAt,
        interval: isReportSchedule.interval,
      });
    } else {
      return getAggregatedLength({
        startAt: filter.startAt,
        endAt: filter.endAt,
        interval: filter.interval,
      });
    }
  }, [filter, schema, isReportSchedule]);

  return (
    <>
      {!isReportSchedule && (
        <ChartHeader $showTitle={isShowTitle} $compact={compactControls}>
          {isShowTitle && (
            <WrapperTitle $compact={compactControls}>
              <Title>
                {compactControls
                  ? 'RPM vs Fuel Cons. vs Speed vs AE Running Time'
                  : 'RPM vs Fuel Consumption vs Speed vs AE Running Time'}
              </Title>
              <Subtitle $compact={compactControls}>
                Value of line chart with the dotted line is only estimated data
                because the data is not yet available
              </Subtitle>
            </WrapperTitle>
          )}

          {compactControls ? (
            <MobileChartMode
              block
              value={activeChartType}
              onChange={(value) => setActiveChartType(String(value))}
              options={[
                { label: 'Default', value: 'default' },
                { label: 'Line Chart', value: 'line' },
              ]}
            />
          ) : (
            <Select
              value={activeChartType}
              style={{ width: 120 }}
              placeholder='Chart type'
              onChange={(value) => setActiveChartType(value)}
              options={[
                { label: 'Default', value: 'default' },
                { label: 'Line', value: 'line' },
              ]}
            />
          )}
        </ChartHeader>
      )}

      <ReportChart
        height={height}
        logs={logs}
        filter={isReportSchedule || filter}
        yAxis={yAxis}
        chartSchema={schema}
        compact={compactControls}
        isReportSchedule={
          isReportSchedule && isReportSchedule.deviceType ? true : false
        }
        aggregatedLength={aggregatedLength}
        sumRunningTime
        runningTimeAt={aeRunningTimeAt}
        precomputedSeries={precomputed?.series}
      />

      {isReportSchedule && tableColumns.length && (
        <>
          {tables.map((table, tableIdx) => (
            <div key={tableIdx} style={{ marginTop: tableIdx > 0 ? '14px' : '0' }}>
              {dataTable.is48HourMode && isReportSchedule && (
                <div style={{ textAlign: 'center', fontWeight: 'bold', fontSize: '7px', marginBottom: '2px' }}>
                  {dayjs(isReportSchedule.startAt * 1000)
                    .utcOffset(reportTimezone * 60)
                    .add(tableIdx, 'day')
                    .format('DD MMM YYYY')}
                </div>
              )}
              <TableSummary columnCount={dataTable.columnCount} is48HourMode={dataTable.is48HourMode}>
                <tbody>
                  {table.map((data, idx) => (
                    <tr key={idx}>
                      <td>{tableColumns[idx]}</td>
                      {data.map((item: number | string | undefined, idx2: number) => (
                        <td key={idx2}>
                          {typeof item === 'undefined' || item === null
                            ? '-'
                            : idx === 0 || idx === table.length - 1
                            ? item
                            : typeof item === 'number'
                            ? item.toFixed(2)
                            : item}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </TableSummary>
            </div>
          ))}
        </>
      )}
    </>
  );
};

const TableSummary = styled.table<{ columnCount: number; is48HourMode: boolean }>`
  width: 100%;
  margin: ${props => props.is48HourMode ? '5px auto 0' : '0 auto'};
  table-layout: fixed;
  font-size: ${props => props.is48HourMode ? '6px' : props.columnCount > 10 ? '8px' : '10px'};
  td {
    border: thin solid #ddd;
    padding: ${props => props.is48HourMode ? '1px' : props.columnCount > 10 ? '4px 1px' : '6px 2px'};
    font-size: inherit;
    text-align: center;
  }
  tr:first-child {
    background: #d1d1d1;
  }
  td:first-child {
    width: ${props => props.is48HourMode ? '45px' : props.columnCount > 10 ? '60px' : '80px'};
    background: #142d79;
    color: white;
    border-color: #142d79;
  }
`;

const SectionTitle = styled.div`
  font-size: 14px;
  font-weight: bold;
  margin: 20px 0 10px 0;
  padding: 8px;
  background: #f5f5f5;
  border-left: 4px solid #142d79;
`;

const ChartHeader = styled.div<{
  $showTitle?: boolean;
  $compact?: boolean;
}>`
  margin-top: 8px;
  display: flex;
  align-items: center;
  justify-content: ${({ $showTitle }) =>
    $showTitle ? 'space-between' : 'flex-start'};

  ${({ $compact }) =>
    $compact &&
    `
      flex-direction: column;
      align-items: stretch;
      gap: 12px;
      margin-top: 0;
    `}
`;

const WrapperTitle = styled.div<{ $compact?: boolean }>`
  margin-bottom: 20px;
  margin-left: 10px;

  ${({ $compact }) =>
    $compact &&
    `
      margin: 0;
    `}
`;

const Title = styled.div`
  font-size: 16px;
  font-weight: 600;
  line-height: 1.2;
`;

const Subtitle = styled.div<{ $compact?: boolean }>`
  color: #768499;
  margin-top: 5px;
  line-height: 1.5;

  ${({ $compact }) =>
    $compact &&
    `
      margin-top: 8px;
      font-size: 12px;
    `}
`;

const MobileChartMode = styled(Segmented)`
  width: 100%;
  padding: 4px;
  border: 1px solid #e8ecf3;
  border-radius: 12px;
  background: #e8ecf3;

  .ant-segmented-item {
    min-height: 41px;
    display: grid;
    place-items: center;
    border-radius: 8px;
    color: #1e1e1e;
    font-size: 14px;
    line-height: 1.2;
  }

  .ant-segmented-item-label {
    min-height: 41px;
    line-height: 41px;
    padding: 0 16px;
  }

  .ant-segmented-item-selected {
    border-radius: 8px;
    background: #ffffff;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
  }

  .ant-segmented-thumb {
    border-radius: 8px;
    background: #ffffff;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
  }
`;

export default ChartRpmFuelSpeedRunning;
